/**
 * Which door My profile is knocking on — `docs/identity-and-sign-in.md` §3,
 * Aaron's item 4.
 *
 * "It should be a screen that is technically separate of environment as this
 * is one user account that has been granted permissions to multiple
 * environments... it needs to be the same user management for all users."
 *
 * One profile surface, two apps. The two differ in exactly two things and this
 * is both of them:
 *
 *   - **The request client.** `lib/api.ts` prefixes `/api/v1`,
 *     `lib/admin-api.ts` prefixes `/api/v1/platform`, and the server mounts
 *     the same relative paths on both sides (`routes/platform-me.ts`). So the
 *     profile asks for `/me/methods` and lands wherever it was opened.
 *   - **The step-up.** A tenant user re-types their password; a Rowted admin
 *     types a six-digit code against a different endpoint. `guard(fn)` hides
 *     which: it runs `fn`, and on `step_up_required` it asks, then **replays**
 *     `fn` rather than asking for the click again.
 *
 * A context rather than props because the surface is a dozen cards deep and
 * threading two values through all of them is a dozen chances to pass the
 * wrong one. The provider owns the step-up dialog, so a caller never renders
 * it and never forgets to.
 */
import { createContext, useContext, type ReactNode } from 'react'

export type AccountRequest = <T>(
  path: string,
  init?: { method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'; body?: unknown },
) => Promise<T>

export interface AccountApi {
  request: AccountRequest
  /**
   * Run something that may need proving first.
   *
   * Resolves true when it ran, false when the person closed the dialog. Every
   * other failure is thrown, because it belongs to the action rather than to
   * the credential.
   */
  guard: (action: () => Promise<void>) => Promise<boolean>
  /** 'tenant' or 'platform' — what a card says rather than what it calls. */
  side: 'tenant' | 'platform'
}

const Context = createContext<AccountApi | null>(null)

export function AccountApiProvider({ value, children }: {
  value: AccountApi
  children: ReactNode
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useAccountApi(): AccountApi {
  const found = useContext(Context)
  if (!found) {
    throw new Error('A profile card was drawn outside <AccountApiProvider>.')
  }
  return found
}
